// SMS alerts: text one person on the event, or everyone in one of its teams,
// when a fetch changes things materially — anything but "much the same", or a
// point coming into the forecast's range. Rules in app/safety/notify.py. The
// people and teams come from the safety router's own list (notify-options), so
// this needs no attendance permissions.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, MenuItem, Radio, RadioGroup, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { SAFETY_API } from "./format";

export default function NotifyDialog({ open, assessment: a, onClose, onSaved }) {
  const fullScreen = useFullScreenDialog();
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({ enabled: false, kind: "person", personId: "", teamId: "", manual: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      enabled: !!a.notify_enabled,
      kind: a.notify_team_id ? "team" : "person",
      personId: a.notify_personnel_id || "",
      teamId: a.notify_team_id || "",
      manual: !!a.notify_manual,
    });
    setOptions(null);
    axios.get(`${SAFETY_API}/assessments/${a.id}/notify-options`)
      .then(res => setOptions(res.data)).catch(() => setOptions({ people: [], teams: [] }));
  }, [open, a.id, a.notify_enabled, a.notify_personnel_id, a.notify_team_id, a.notify_manual]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const person = options?.people.find(p => p.id === form.personId);
  const team = options?.teams.find(t => t.id === form.teamId);
  const chosen = form.kind === "person" ? !!form.personId : !!form.teamId;
  const valid = !form.enabled || chosen;
  const body = () => ({
    enabled: form.enabled,
    personnel_id: form.kind === "person" ? (form.personId || null) : null,
    team_id: form.kind === "team" ? (form.teamId || null) : null,
    manual: form.manual,
  });
  const saved = a.notify_enabled === form.enabled
    && (a.notify_personnel_id || null) === body().personnel_id
    && (a.notify_team_id || null) === body().team_id;

  const save = () => {
    setSaving(true);
    axios.put(`${SAFETY_API}/assessments/${a.id}/notify`, body())
      .then(res => onSaved(res.data)).catch(() => {}).finally(() => setSaving(false));
  };
  const test = () => {
    setTesting(true);
    axios.post(`${SAFETY_API}/assessments/${a.id}/notify-test`)
      .then(res => {
        const r = res.data;
        if (r.skipped) { notify(r.skipped, "warning"); return; }
        const ok = r.results.filter(x => x.sent);
        const failed = r.results.filter(x => !x.sent);
        notify(`Test text sent to ${ok.map(x => x.name).join(", ") || "nobody"}`
               + (failed.length ? `; failed for ${failed.map(x => `${x.name} (${x.error})`).join(", ")}.` : "."),
               failed.length ? "warning" : "success");
      })
      .catch(() => {})
      .finally(() => setTesting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>SMS alerts</DialogTitle>
      <DialogContent>
        {!options ? <CircularProgress size={24} /> : (
          <Stack spacing={2}>
            {!options.sms_configured && (
              <Alert severity="warning">SMS sending isn't configured, so no text can go out.</Alert>
            )}
            <FormControlLabel control={<Switch checked={form.enabled} onChange={e => set({ enabled: e.target.checked })} />}
              label="Text someone when a fetch changes things" />
            <Typography variant="body2" color="text.secondary">
              A text goes when a fetch is worse, better or mixed compared with the one before, or when
              race day first comes into the forecast's range — never for "much the same", nor for the
              first fetch. It carries the headline and the main changes, so the recipient doesn't need
              to open the app (the link in it only works for those with access to this page).
            </Typography>

            <RadioGroup row value={form.kind} onChange={e => set({ kind: e.target.value })}>
              <FormControlLabel value="person" control={<Radio />} label="One person on the event" disabled={!form.enabled} />
              <FormControlLabel value="team" control={<Radio />} label="Everyone in a team" disabled={!form.enabled} />
            </RadioGroup>

            {form.kind === "person" ? (
              options.people.length === 0 ? (
                <Alert severity="info">Nobody is on this event yet. Add people to the event to choose one.</Alert>
              ) : (
                <TextField select label="Person" value={form.personId} disabled={!form.enabled}
                           onChange={e => set({ personId: e.target.value })}
                           error={!!person && !person.has_phone}
                           helperText={person && !person.has_phone ? "No phone number on their record — the text will fail." : undefined}>
                  {options.people.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{p.role ? ` — ${p.role}` : ""}{p.has_phone ? "" : " (no phone number)"}
                    </MenuItem>
                  ))}
                </TextField>
              )
            ) : (
              options.teams.length === 0 ? (
                <Alert severity="info">This event has no teams. Create them on the event's attendance.</Alert>
              ) : (
                <Box>
                  <TextField select fullWidth label="Team" value={form.teamId} disabled={!form.enabled}
                             onChange={e => set({ teamId: e.target.value })}>
                    {options.teams.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name} ({t.members.length})</MenuItem>
                    ))}
                  </TextField>
                  {team && (
                    <Typography variant="caption" color={team.without_phone ? "warning.main" : "text.secondary"}
                                sx={{ display: "block", mt: 0.5 }}>
                      {team.members.length ? `Members now: ${team.members.join(", ")}.` : "The team has no members yet."}
                      {team.without_phone ? ` ${team.without_phone} without a phone number won't get it.` : ""}
                      {" "}Whoever is in the team when a text goes gets it.
                    </Typography>
                  )}
                </Box>
              )
            )}

            <FormControlLabel disabled={!form.enabled}
              control={<Checkbox checked={form.manual} onChange={e => set({ manual: e.target.checked })} />}
              label={<Typography variant="body2">Also when someone fetches by hand (otherwise scheduled fetches only)</Typography>} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {a.notify_enabled && (
          <Button onClick={test} disabled={testing || !saved} sx={{ mr: "auto" }}
                  title={saved ? undefined : "Save first — the test goes to whoever is saved"}>
            {testing ? "Sending…" : "Send a test text"}
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !valid || !options}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
